import { MAX_SAGITTA, MIN_SAGITTA } from '@/editor/constant/wall';
import { RoomBaseData } from '@/editor/data/room/Room';
import { ESeparateWallType } from '@/editor/data/separateWall/SeparateWall';
import { isArcSeparateWallData } from '@/editor/data/separateWall/utils';
import { ERegionType, ERoomType, RegionInfo } from '@/editor/data/type';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { modelManager } from '@/editor/manager/entityManager';
import { globalStores } from '@/editor/stores';
import { EEntityType, EOperateMode, EToolType, EWallOperateType, ISelected } from '@/editor/types';
import { showToast } from '@/editor/utils/feedback';
import i18n from '@/i18n';
import { EParamsKeys, IEditParams, IEditParamsOnChange, IEditParamsOnReset } from '@/editor/types/editParamType';
import {
  editColumnData,
  editDoorWindowData,
  editVirtualAssemblyData,
  editVirtualColumnData,
  editVirtualFurnitureData,
  resetDoorWindowSize,
  setEditFurnitureEntity
} from '@/editor/utils/editModelUtils';
import { clamp, cloneDeep, throttle } from 'lodash-es';
import EventBus from '@/editor/utils/EventBus';
import { REGION_NAME_BY_TYPE, ROOM_NAME_BY_TYPE } from './const';
import {
  getDefaultParamInfo,
  getParamsChangeBehaviors,
  getTransformingData,
  isDrawSeparateWallMode,
  isDrawWallMode,
  isParamValueUnchanged
} from './utils';
import {
  getDoorWindowEntityParamInfo,
  getMultiSelectedInfo,
  getFurnitureEntityParamInfo,
  getColumnEntityParamInfo,
  getDrawWallParamInfo,
  getWallEntityParamInfo,
  getDrawSeparateWallParamInfo,
  getSeparateWallEntityParamInfo,
  getRegionParamInfo,
  getRoomParamInfo,
  getFaceParamInfo,
  getAssemblyParamInfo,
  wallChangeHandler
} from './module';
import { IModelInfo, IMultiModelData } from '@/editor/data/multiModel/multiModel';
import { IColumnData } from '@/editor/data/model/model';
import type { IFurniture } from '@/editor/data/model/furniture';
import type { IAssemblyData } from '@/editor/data/assembly/assembly';
import { isMultiSelectedSameDoorWindow } from '@/editor/utils/select/multi-select';

export class EditParams {
  // 必须是第一个字段：constructor 里的 setParam() 会写 paramsData 并触发通知
  private _event = new EventBus<{ paramsChange: () => void }>();

  private _paramsData?: IEditParams;
  /** 当前数据的 id ，每次 setParam 调用都会自增，用于异步更新判断 */
  private _index = 0;
  /**
   * 多选进行中的构件列表，null 表示当前不在多选。
   * 多选期间 selected 是空的（多选态由 MultiSelect 控制器自己持有），面板只能靠它算。
   */
  private _multiSelectingModelInfos: IModelInfo[] | null = null;

  /**
   * 节流放在发送端：paramsData 在拖拽期间会被高频重建，
   * 不限流会让参数面板每次 mousemove 都重渲染
   */
  private _notify = throttle(() => this._event.emit('paramsChange'), 100);

  get paramsData() {
    return this._paramsData;
  }
  set paramsData(data: IEditParams | undefined) {
    // 引用比较即可：getXxxParamInfo 每次都返回新对象，所以实际上每次赋值都会通知
    if (this._paramsData === data) return;
    this._paramsData = data;
    this._notify();
  }

  /** 供 useSyncExternalStore 使用，箭头函数保证引用稳定，否则会反复重订阅 */
  subscribe = (listener: () => void) => this._event.on('paramsChange', listener);
  getSnapshot = () => this._paramsData;

  constructor() {
    this.listenOperateEvent();
    this.setParam();
  }

  private listenOperateEvent() {
    editorEvent.on(EditorEventName.OPERATE_MODE_CHANGE, this.setParam);

    editorEvent.on(EditorEventName.SCENE_MODE_CHANGE, this.setParam);

    editorEvent.on(EditorEventName.HISTORY_CHANGE, this.setParam);

    editorEvent.on(EditorEventName.ENTITY_SELECTED, this.setParam);

    editorEvent.on(EditorEventName.PARAM_CHANGE, this.setParam);

    editorEvent.on(EditorEventName.SCHEME_LOADED, this.setParam);

    editorEvent.on(EditorEventName.MULTI_SELECT_DATA_CHANGE, (modelInfos) => {
      this._multiSelectingModelInfos = modelInfos;
      this.setParam();
    });
  }

  private _notifyRerender() {
    editorEvent.emit(EditorEventName.REFRESH_SELECTED);
    modelManager.selectedRerender();
  }

  private setParam = () => {
    const selected = globalStores.editorStateStore.selected;
    const operateMode = globalStores.operateInfo.mode;

    if (isDrawWallMode(operateMode)) {
      this.paramsData = getDrawWallParamInfo(operateMode);
    } else if (isDrawSeparateWallMode(globalStores.operateInfo)) {
      this.paramsData = getDrawSeparateWallParamInfo(globalStores.operateInfo);
    } else if (operateMode === EOperateMode.DrawStruct) {
      this.setVirtualModelEntity();
    } else if (operateMode === EOperateMode.DrawFurniture) {
      this.setVirtualFurnitureEntity();
    } else if (operateMode === EOperateMode.DrawAssembly) {
      this.setVirtualAssemblyEntity();
    } else if (operateMode === EOperateMode.Array) {
      this.paramsData = getDefaultParamInfo();
    } else if (selected.entityId) {
      this.setSelectedParam();
    } else if (operateMode === EOperateMode.MultiSelect) {
      this.setMultiSelectingParam();
    } else if (
      operateMode === EOperateMode.EMPTY ||
      operateMode === EOperateMode.DrawDoorWindow ||
      globalStores.operateInfo.toolType === EToolType.virtualSeparateWall
    ) {
      this.paramsData = getDefaultParamInfo();
    }
    this._index++;
  };

  private setVirtualModelEntity() {
    const columnData = globalStores.editorStateStore.columnVirtualData;
    if (columnData) {
      this.paramsData = getColumnEntityParamInfo(columnData, EOperateMode.DrawStruct);
    }
  }

  private setVirtualFurnitureEntity() {
    const modelId = globalStores.operateInfo.extraState?.furnitureData?.modelId;
    if (!modelId) return;
    const modelInfo = globalStores.modelStore.getModel(modelId);
    if (!modelInfo) return;
    if (
      !globalStores.editorStateStore.furnitureVirtualData ||
      globalStores.editorStateStore.furnitureVirtualData.modelId !== modelInfo?.id
    ) {
      globalStores.editorStateStore.furnitureVirtualData = {
        id: '',
        modelId: modelInfo.id,
        center: { x: 0, y: 0 },
        offGround: modelInfo?.properties.offGround || 0,
        width: modelInfo?.properties.width || 0,
        thickness: modelInfo?.properties.thickness || 0,
        height: modelInfo?.properties.height || 0,
        rotation: modelInfo?.properties.rotationZ || 0
      };
    }

    this.paramsData = getFurnitureEntityParamInfo(globalStores.editorStateStore.furnitureVirtualData, true);
  }

  /**
   * 放置中的虚拟组合。数据由 AssemblyOperation.copyAssembly 写入 assemblyVirtualData，
   * 这里只读取，面板的改动经 editVirtualAssemblyData 写回同一份数据
   */
  private setVirtualAssemblyEntity() {
    const assemblyData = globalStores.editorStateStore.assemblyVirtualData;
    if (!assemblyData) return;

    this.paramsData = getAssemblyParamInfo(assemblyData);
  }

  /**
   * 多选进行中的面板。selected 已被清空，改用控制器同步过来的构件列表。
   *
   * 少了这个分支，多选期间任何一次 setParam 都会一个分支都不命中，把面板冻在进入多选之前的内容。
   */
  private setMultiSelectingParam() {
    const modelInfos = this._multiSelectingModelInfos ?? [];

    // 还没框中任何构件（含刚起手拉框、框空后拖回），面板保持原样，不闪成空白。
    // 退出多选时 exit() 会把 operateMode 切回 EMPTY，那时才由兜底分支收敛到默认面板
    if (!modelInfos.length) return;

    // 只剩一个构件时视作普通单选，展示它自身的属性。多选落定后本来就会降级成单选
    // （2D 见 operateEntityController.handleEntityPicked，3D 见 MultiSelect3d.applySelected），
    // 这里跟着降级，进行中与落定后的表现才一致
    if (modelInfos.length === 1) {
      const [{ id, type }] = modelInfos;
      this.setSelectedParam({ entityId: id, type });
      return;
    }

    this.paramsData = getMultiSelectedInfo(modelInfos);
  }

  private setSelectedParam(selected: ISelected = globalStores.editorStateStore.selected) {
    if (selected && selected.entityId) {
      switch (selected.type) {
        case EEntityType.Wall: {
          this.setEditWallEntity(selected.entityId);
          break;
        }
        case EEntityType.DoorWindow:
        case EEntityType.Column:
        case EEntityType.SeparateWall: {
          this.setEditModelEntity(selected);
          break;
        }
        case EEntityType.Room: {
          this.setEditRoomEntity(selected.entityId);
          break;
        }
        case EEntityType.Region: {
          this.setEditRegionEntity(selected.entityId);
          break;
        }
        case EEntityType.Furniture: {
          this.setEditFurnitureEntity(selected.entityId);
          break;
        }
        case EEntityType.Face: {
          this.setFaceEntity(selected.entityId);
          break;
        }
        case EEntityType.Assembly: {
          this.setAssembly(selected.entityId);
          break;
        }
        case EEntityType.MultiModel: {
          this.paramsData = getMultiSelectedInfo();
          break;
        }
        default: {
          this.setEditSpace();
          break;
        }
      }
    } else {
      this.setEditSpace();
    }
  }

  private setEditWallEntity(wallId: string) {
    this.paramsData = getWallEntityParamInfo(wallId);
  }

  private setEditRoomEntity(roomId: string) {
    this.paramsData = getRoomParamInfo(roomId);
  }

  private setEditRegionEntity(regionId: string) {
    this.paramsData = getRegionParamInfo(regionId);
  }

  private setEditSpace() {
    this.paramsData = getDefaultParamInfo();
  }

  private setEditFurnitureEntity(furnitureId: string) {
    const furnitureData =
      getTransformingData<IFurniture>(furnitureId, EEntityType.Furniture) ||
      globalStores.modelStore.getFurnitureData(furnitureId);
    if (!furnitureData) return;
    this.paramsData = getFurnitureEntityParamInfo(furnitureData);
  }

  private setFaceEntity(faceId: string) {
    this.paramsData = getFaceParamInfo(faceId);
  }

  private setEditModelEntity(selected: ISelected) {
    const { entityId, type } = selected;
    if (type === EEntityType.DoorWindow) {
      this.paramsData = getDoorWindowEntityParamInfo(entityId);
    } else if (type === EEntityType.Column) {
      const columnData =
        getTransformingData<IColumnData>(entityId, EEntityType.Column) ||
        globalStores.structStore.getColumnData(entityId);
      if (columnData) {
        this.paramsData = getColumnEntityParamInfo(columnData, EEntityType.Column);
      }
    } else if (type === EEntityType.SeparateWall) {
      this.paramsData = getSeparateWallEntityParamInfo(entityId);
    }
  }

  private setAssembly(assemblyId: string) {
    const assemblyData =
      getTransformingData<IAssemblyData>(assemblyId, EEntityType.Assembly) ||
      globalStores.assemblyStore.getAssemblyData(assemblyId);
    if (!assemblyData) return;
    this.paramsData = getAssemblyParamInfo(assemblyData);
  }

  public onChange: IEditParamsOnChange = (params) => {
    switch (params.type) {
      case EEntityType.Wall: {
        this.handleWallChange(params);
        break;
      }
      case EEntityType.Room: {
        this.handleRoomChange(params);
        break;
      }
      case EEntityType.Region: {
        this.handleRegionChange(params);
        break;
      }
      case EEntityType.Column:
      case EEntityType.DoorWindow:
      case EOperateMode.DrawStruct: {
        this.handleModelChange(params);
        break;
      }
      case EEntityType.Furniture: {
        this.handleFurnitureChange(params);
        break;
      }
      case EEntityType.SeparateWall:
        this.handleSeparateWallChange(params);
        break;
      case EEntityType.Assembly:
        this.handleAssemblyChange(params);
        break;
      case EOperateMode.DrawWall:
      case EOperateMode.DrawRectWall:
      case EOperateMode.DrawArcWall: {
        this.handleDrawWallParamChange(params);
        break;
      }
      case EOperateMode.DrawSeparateWall:
      case EOperateMode.DrawArcSeparateWall: {
        this.handleDrawSeparateWallParamChange(params);
        break;
      }
      case EOperateMode.MultiSelect: {
        this.handelMultiSelectedChange(params);
        break;
      }
      case EOperateMode.DrawDoorWindow:
      case EOperateMode.EMPTY: {
        this.handleDefaultChange(params);
        break;
      }
    }
  };

  // reset是一种特殊的onChange,但不需要那么多props，故暂时做精简
  public onReset: IEditParamsOnReset = (params) => {
    switch (params.type) {
      case EEntityType.DoorWindow:
        this.resetDoorOrWindow(params);
        break;
      case EEntityType.Furniture:
        this.resetFurniture(params);
        break;
      default:
        // nothing to do
        break;
    }
  };

  handleWallChange: IEditParamsOnChange = (params) => {
    const currentIndex = this._index;
    wallChangeHandler(params)
      .then(() => {
        if (params.key === EParamsKeys.sagitta) editorEvent.emit(EditorEventName.REFRESH_SELECTED);
        // 避免异步回调时，参数面板已经刷新了
        if (this._index !== currentIndex) {
          return;
        }
        this.paramsData = getWallEntityParamInfo(params.id);
      })
      // 墙体已被删除时 wallChangeHandler 会 reject，此时本就无事可做
      .catch(() => undefined);
    // switch (params.key) {
    //   case EParamsKeys.thickness: {
    //     if (params.values[EParamsKeys.thickness] === params.value) {
    //       this.paramsData = getWallEntityParamInfo(params.id);
    //       return;
    //     }
    //     const currentIndex = this._index;
    //     const value = Math.round(Number(params.value));
    //     globalStores.wallOperation.modifyWallBaseData(params.id, { thickness: value }).then(() => {
    //       // 避免异步回调时，参数面板已经刷新了
    //       if (this._index !== currentIndex) {
    //         return;
    //       }
    //       this.paramsData = getWallEntityParamInfo(params.id);
    //     });
    //     break;
    //   }
    //   case EParamsKeys.sagitta: {
    //     break;
    //   }
    //   default:
    //     break;
    // }
  };

  handleSeparateWallChange: IEditParamsOnChange = (params) => {
    switch (params.key) {
      case EParamsKeys.type: {
        globalStores.separateWallOperation.modifySeparateWallType(params.id, params.value as ESeparateWallType);
        break;
      }
      case EParamsKeys.thickness: {
        const value = Math.round(Number(params.value));
        globalStores.separateWallOperation.modifySeparateWalls([
          {
            id: params.id,
            data: {
              thickness: value
            }
          }
        ]);
        break;
      }
      case EParamsKeys.sagitta: {
        this.handleSeparateWallSagittaChange(params);
        return;
      }
    }
    this.paramsData = getSeparateWallEntityParamInfo(params.id);
  };

  // 修改弧形隔墙的弦高，通过弧中点操作走合法性校验流程（与标尺修改一致）
  private handleSeparateWallSagittaChange: IEditParamsOnChange = (params) => {
    if (isParamValueUnchanged(params)) {
      return;
    }
    const sepData = globalStores.separateWallStore.getSepData(params.id, false);
    if (!sepData || !isArcSeparateWallData(sepData)) {
      this.paramsData = getSeparateWallEntityParamInfo(params.id);
      return;
    }

    const currentArc = new ArcSeg2d({
      start: sepData.start,
      end: sepData.end,
      center: sepData.center,
      clockwise: sepData.clockwise
    });
    const sagitta =
      clamp(Math.round(Number(params.value)), MIN_SAGITTA, MAX_SAGITTA) * Math.sign(currentArc.getSagitta());
    const newArc = new ArcSeg2d({ start: sepData.start, end: sepData.end, sagitta });

    const currentIndex = this._index;
    void Promise.resolve(
      globalStores.separateWallOperation.moveSeparateWallPoint(params.id, {
        type: EWallOperateType.ArcMidPoint,
        point: newArc.getMidPoint()
      })
    ).then((isSuccess) => {
      if (!isSuccess) {
        showToast(i18n.t('draw.warning.sep_wall_intersecting'));
      }
      // 避免异步回调时，参数面板已经刷新了
      if (this._index !== currentIndex) {
        return;
      }
      this.paramsData = getSeparateWallEntityParamInfo(params.id);
    });
  };

  handleRoomChange: IEditParamsOnChange = (params) => {
    const roomData = globalStores.roomStore.getRoomData(params.id);
    if (roomData) {
      switch (params.key) {
        case EParamsKeys.type: {
          const value = Number(params.value);
          if (params.values[EParamsKeys.type] === value) {
            return;
          }
          const modifyInfo: Partial<RegionInfo> = {};
          // 修改的分区的名称就替换掉之前的名称
          modifyInfo[EParamsKeys.name] =
            ROOM_NAME_BY_TYPE[params.value as ERoomType] || (params.values[EParamsKeys.name] as string);
          modifyInfo[EParamsKeys.type] = value;
          globalStores.roomOperation.modifyRoomBaseData(params.id, {
            roomInfo: {
              ...roomData.roomInfo,
              ...modifyInfo
            }
          } as RoomBaseData);
          this.paramsData = getRoomParamInfo(params.id);

          break;
        }
        case EParamsKeys.name: {
          const value = String(params.value);
          if (params.values[EParamsKeys.name] === value) {
            return;
          }
          if (value.trim().length > 0) {
            globalStores.roomOperation.modifyRoomBaseData(params.id, {
              roomInfo: {
                ...roomData.roomInfo,
                [EParamsKeys.name]: value
              }
            });
          }
          this.paramsData = getRoomParamInfo(params.id);
          break;
        }
      }
    }
  };

  handleRegionChange: IEditParamsOnChange = (params) => {
    const regionData = globalStores.regionStore.getRegionData(params.id);
    if (regionData) {
      const isRegion = globalStores.regionStore.getRoomRegions(regionData.relateRoomId).length > 1;
      switch (params.key) {
        case EParamsKeys.type: {
          const value = Number(params.value);
          if (params.values[EParamsKeys.type] === value) {
            return;
          }
          const modifyInfo: Partial<RegionInfo> = {};
          // 修改的分区的名称就替换掉之前的名称
          modifyInfo[EParamsKeys.name] =
            (isRegion
              ? REGION_NAME_BY_TYPE[params.value as ERegionType]
              : ROOM_NAME_BY_TYPE[params.value as ERoomType]) || (params.values[EParamsKeys.name] as string);
          globalStores.regionOperation.modifyRegionInfo(params.id, {
            ...modifyInfo,
            [EParamsKeys.type]: value
          });
          this.paramsData = getRegionParamInfo(params.id);

          break;
        }
        case EParamsKeys.name: {
          const value = String(params.value);
          if (params.values[EParamsKeys.name] === value) {
            return;
          }
          if (value.trim().length > 0) {
            globalStores.regionOperation.modifyRegionInfo(params.id, {
              [EParamsKeys.name]: value
            });
          }
          this.paramsData = getRegionParamInfo(params.id);
          break;
        }
      }
    }
  };

  handleDefaultChange: IEditParamsOnChange = (params) => {
    const value = Math.round(Number(params.value));
    globalStores.compositeOperation.setLayerHeight(value);
    this.paramsData = getDefaultParamInfo();
  };

  handleDrawWallParamChange: IEditParamsOnChange = (params) => {
    if (params.key === EParamsKeys.thickness) {
      const value = Math.round(Number(params.value));
      if (params.values[EParamsKeys.thickness] === value) {
        this.paramsData = getDrawWallParamInfo(globalStores.operateInfo.mode);
        return;
      }

      globalStores.compositeOperation.setNotHistoryParams({
        wallThickness: value
      });

      if (isDrawWallMode(globalStores.operateInfo.mode)) {
        this.paramsData = getDrawWallParamInfo(globalStores.operateInfo.mode);
      }
    }
  };

  handleDrawSeparateWallParamChange: IEditParamsOnChange = (params) => {
    if (params.key === EParamsKeys.thickness) {
      if (params.values[EParamsKeys.thickness] === params.value) {
        if (isDrawSeparateWallMode(globalStores.operateInfo)) {
          this.paramsData = getDrawSeparateWallParamInfo(globalStores.operateInfo);
        }
        return;
      }
      const value = Math.round(Number(params.value));

      globalStores.compositeOperation.setNotHistoryParams({
        separateWallThickness: value
      });
    } else if (params.key === EParamsKeys.type) {
      const value = params.value as ESeparateWallType;
      if (params.values[EParamsKeys.type] === value) {
        return;
      }
      globalStores.compositeOperation.setNotHistoryParams({
        separateWallType: value
      });
    }

    if (isDrawSeparateWallMode(globalStores.operateInfo)) {
      this.paramsData = getDrawSeparateWallParamInfo(globalStores.operateInfo);
    }
  };

  handleFurnitureChange: IEditParamsOnChange = (params) => {
    const value = Number(params.value);
    const behaviors: EParamsKeys[] = [];

    const behavior = params.key;
    let mainBehavior: EParamsKeys;
    if (behavior !== EParamsKeys.empty) {
      mainBehavior = behavior;
      behaviors.push(behavior);
    } else {
      return;
    }

    if (params.linked) {
      for (const link of params.linked) {
        const behavior = link;
        if (behavior !== EParamsKeys.empty && mainBehavior !== behavior) {
          behaviors.push(behavior);
        }
      }
    }

    if (behaviors.length) {
      if (globalStores.operateInfo.mode === EOperateMode.DrawFurniture) {
        editVirtualFurnitureData(behaviors, value);
        this.setVirtualFurnitureEntity();
      } else {
        setEditFurnitureEntity(params.id, behaviors, value);
        this.setEditFurnitureEntity(params.id);
        this._notifyRerender();
      }
    }
  };

  handleModelChange: IEditParamsOnChange = (params) => {
    const value = Number(params.value);
    const behaviors = getParamsChangeBehaviors(params);

    if (behaviors?.length) {
      if (params.type === EEntityType.DoorWindow) {
        editDoorWindowData(params.id, behaviors, value);
        this.paramsData = getDoorWindowEntityParamInfo(params.id);
      } else if (params.type === EEntityType.Column) {
        editColumnData(params.id, behaviors, value);
        const columnData = globalStores.structStore.getColumnData(params.id);
        if (columnData) {
          this.paramsData = getColumnEntityParamInfo(columnData, params.type);
          this._notifyRerender();
        }
      } else if (params.type === EOperateMode.DrawStruct) {
        editVirtualColumnData(behaviors, value);
        const columnData = globalStores.editorStateStore.columnVirtualData;
        if (columnData) {
          this.paramsData = getColumnEntityParamInfo(columnData, params.type);
        }
      }
    }
  };

  handleAssemblyChange: IEditParamsOnChange = (params) => {
    let value = Number(params.value);
    const effectiveBehaviors = [EParamsKeys.offGround, EParamsKeys.rotation];

    const behavior = params.key;
    if (!effectiveBehaviors.includes(behavior)) {
      return;
    }

    if (globalStores.operateInfo.mode === EOperateMode.DrawAssembly) {
      editVirtualAssemblyData([behavior], value);
      this.setVirtualAssemblyEntity();
      return;
    }

    const assemblyData = globalStores.assemblyStore.getAssemblyData(params.id);
    if (!assemblyData) return;
    if (behavior === EParamsKeys.rotation) {
      value = value * (Math.PI / 180);
      globalStores.assemblyOperation.rotateAssembly(params.id, value);
    } else {
      globalStores.assemblyOperation.modifyAssembly(params.id, { ...assemblyData, [behavior]: value });
    }

    this.setAssembly(params.id);
    this._notifyRerender();
  };

  handelMultiSelectedChange: IEditParamsOnChange = (params) => {
    const value = Number(params.value);
    const selected = globalStores.editorStateStore.selected;
    const multiData = globalStores.getDataByIdType(selected.entityId, EEntityType.MultiModel) as IMultiModelData | null;
    if (multiData && isMultiSelectedSameDoorWindow(multiData)) {
      const behaviors = getParamsChangeBehaviors(params);
      if (behaviors?.length) {
        globalStores.multiModelOperation.modifyMultiDoorWindow(multiData.modelInfos, behaviors, value);
        this.paramsData = getMultiSelectedInfo();
      }
    }
  };

  resetFurniture: IEditParamsOnReset = (params) => {
    const furnitureData = globalStores.modelStore.getFurnitureData(params.id);
    if (!furnitureData) return;
    const modelInfo = globalStores.modelStore.getModel(furnitureData.modelId);
    if (!modelInfo) return;

    if (
      furnitureData.thickness !== modelInfo.properties.thickness ||
      furnitureData.width !== modelInfo.properties.width ||
      furnitureData.height !== modelInfo.properties.height
    ) {
      const target = cloneDeep(furnitureData);
      target.thickness = modelInfo.properties.thickness;
      target.width = modelInfo.properties.width;
      target.height = modelInfo.properties.height;

      globalStores.furnitureOperation.modifyFurniture(target.id, target);
      this.setEditFurnitureEntity(furnitureData.id);

      modelManager.selectedRerender();
    }
  };

  resetDoorOrWindow: IEditParamsOnReset = (params) => {
    const currentDoorWindowData = globalStores.modelStore.getWindowData(params.id);
    const modelId = currentDoorWindowData?.modelId;
    if (!modelId) return;
    const modelInfo = globalStores.modelStore.getModel(modelId);
    if (!modelInfo) return;

    const defaultWidth = modelInfo.properties.width;
    const defaultHeight = modelInfo.properties.height;

    // 重置宽高
    if (currentDoorWindowData?.width !== defaultWidth || currentDoorWindowData?.height !== defaultHeight) {
      resetDoorWindowSize(params.id, defaultWidth, defaultHeight);
      this.paramsData = getDoorWindowEntityParamInfo(params.id);
    }
  };
}
